import { useEffect, useState, useCallback } from 'react'
import { api, fixImageUrl } from '../api'
import CertifsSection from './producer/CertifsSection'
import CoordonneesSection from './producer/CoordonneesSection'
import PresentationGuidee from './producer/PresentationGuidee'
import RinaContextCard from '../components/RinaContextCard'
import { useActionsPortail } from '../contexts/navigation'
import PageHub from '../components/PageHub'
import { MVP } from '../mvp'
import { partagerProducteur } from '../lib/partage'
import { ouvrirModaleVisuel } from '../components/producteur/ModaleVisuel'

/**
 * Mon organisation (Compte → lentille producteur) — v7.1 sujet 4, renommee en
 * S61 : le producteur cherche « son organisation », pas un second profil a cote
 * de « Mon profil ». L'ancienne page « Mon organisation » (autre design, hors
 * prototype) sort du menu ; ses champs descendent ici via `CoordonneesSection`.
 * MÊME source que la fiche client : l'aperçu est construit depuis l'endpoint
 * PUBLIC /public/producteurs/:id (identité + production dérivée des offres
 * canal Marché + badges certifs validées) → aucune divergence possible.
 * L'édition ne touche que l'identité durable : photo, nom, commune, présentation.
 */

const AvatarExploit = ({ photo, size = 64 }) => (
  <span
    className="rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
    style={{
      width: size, height: size,
      background: 'linear-gradient(135deg,rgb(var(--c-teal)),#0d6b5f)',
      border: '2px solid rgb(var(--c-creme) / 0.5)',
      fontSize: size / 2.1,
    }}
  >
    {photo ? <img src={fixImageUrl(photo)} alt="" className="w-full h-full object-cover" /> : '🧑🏾‍🌾'}
  </span>
)

function ContenuProfilProducteur() {
  const { setHubTab } = useActionsPortail()
  const onOpenOffers = () => setHubTab('offers')
  const [org, setOrg] = useState(null)
  const [fiche, setFiche] = useState(null) // vue client exacte (/public/producteurs/:id)
  const [certifs, setCertifs] = useState([])
  const [loading, setLoading] = useState(true)
  const [edit, setEdit] = useState(false)
  const [form, setForm] = useState({ nomCommercial: '', ville: '', description: '' })
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  // Partage (S62) : feuille native ou copie du lien, avec un mot de retour
  const [flash, setFlash] = useState(null)
  const partager = async () => {
    const r = await partagerProducteur({ id: org?.id, nom, description: org?.description })
    const msg = r === 'copie' ? 'Lien copié — collez-le où vous voulez !' : r === 'echec' ? 'Impossible de partager sur cet appareil' : null
    if (msg) { setFlash(msg); setTimeout(() => setFlash(null), 2800) }
  }

  const load = useCallback(async () => {
    try {
      const me = await api.producer.getMe()
      setOrg(me)
      setForm({
        nomCommercial: me?.nomCommercial || me?.nom || '',
        ville: me?.ville || '',
        description: me?.description || '',
      })
      const [f, c] = await Promise.all([
        me?.id ? api.getProducteur(me.id) : Promise.resolve(null),
        api.producer.getCertifications(),
      ])
      setFiche(f)
      setCertifs(c || [])
    } catch {
      setError('Impossible de charger votre profil')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.producer.updateMe({
        nomCommercial: form.nomCommercial,
        ville: form.ville,
        description: form.description,
      })
      await load()
      setEdit(false)
    } catch (err) {
      setError(err.message || "Erreur lors de l'enregistrement")
    } finally {
      setSaving(false)
    }
  }

  const uploadPhoto = async (file) => {
    if (!file) return
    setUploading(true)
    try {
      await api.producer.uploadLogo(file)
      await load()
    } catch (err) {
      setError(err.message || 'Erreur lors de la photo')
    } finally {
      setUploading(false)
    }
  }

  const removePhoto = async () => {
    setUploading(true)
    try {
      await api.producer.updateMe({ logo: '' })
      await load()
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-9 w-28 rounded-full bg-ink/5" />
        <div className="h-44 rounded-[28px] bg-ink/5" />
        <div className="h-28 rounded-[20px] bg-ink/5" />
      </div>
    )
  }

  const nom = fiche?.nomCommercial || fiche?.nom || org?.nomCommercial || org?.nom || 'Mon exploitation'
  const produits = fiche?.offers || []
  const badges = fiche?.certifications || []

  return (
    <div>
      {/* v7.2.4 : titre + message renard dans la bulle RINA (le retour vit dans
          le header) ; bouton ✏️ Modifier en pastille glass alignee a droite. */}
      <RinaContextCard
       
        title={edit ? 'Modifier ma page' : 'Ma page'}
        message="Voici exactement la fiche que le client voit sur un marché. Votre production se met à jour toute seule depuis vos offres au canal Marché. Vos coordonnées légales, en bas, restent privées."
      />
      {flash && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg animate-fade-up text-white bg-green">
          {flash}
        </div>
      )}
      <div className="flex justify-end gap-2 mb-2.5 px-0.5">
        {!edit && org?.id && (
          <>
            <button
              onClick={() => ouvrirModaleVisuel({ id: org.id })}
              className="glass-surface rounded-full px-3.5 py-[7px] text-[12.5px] font-bold text-green"
            >
              🖼️ Visuel
            </button>
            <button
              onClick={partager}
              className="rounded-full px-3.5 py-[7px] text-[12.5px] font-extrabold text-green bg-primary"
            >
              ↗ Partager
            </button>
          </>
        )}
        <button
          onClick={() => setEdit((v) => !v)}
          className="glass-surface rounded-full px-3.5 py-[7px] text-[12.5px] font-bold"
          style={{ color: 'rgb(var(--c-primary))' }}
        >
          {edit ? 'Terminé' : '✏️ Modifier'}
        </button>
      </div>

      {!edit && (
        <>
          {/* ===== APERÇU = vue client (même endpoint que la fiche publique) ===== */}
          <div className="glass-dark rounded-[28px] p-6 relative overflow-hidden mb-4" style={{ color: 'rgb(var(--c-creme))' }}>
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgb(var(--c-teal) / 0.4), transparent 70%)' }} />
            <div className="relative flex items-center gap-3.5">
              <AvatarExploit photo={fiche?.logo || org?.logo} />
              <div className="min-w-0">
                <p className="font-serif text-[24px] leading-tight">{nom}</p>
                {(fiche?.ville || org?.ville) && (
                  <p className="text-[12.5px] mt-0.5" style={{ color: 'rgb(var(--c-creme) / 0.75)' }}>
                    📍 {fiche?.ville || org?.ville}
                  </p>
                )}
              </div>
            </div>
            {badges.length > 0 && (
              <div className="relative flex gap-1.5 mt-4 flex-wrap">
                {badges.map((b) => (
                  <span key={b.code}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-full"
                    style={{ color: 'rgb(var(--c-creme))', background: 'rgb(var(--c-creme) / 0.14)', border: '1px solid rgb(var(--c-creme) / 0.3)' }}>
                    {b.emoji} {b.nom}
                  </span>
                ))}
              </div>
            )}
          </div>

          {(fiche?.description || org?.description) && (
            <>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2 px-0.5" style={{ color: 'rgb(var(--c-teal))' }}>
                Le producteur
              </p>
              <div className="glass-surface rounded-[20px] p-4 mb-4">
                <p className="text-[13.5px] leading-relaxed text-ink/80">{fiche?.description || org?.description}</p>
              </div>
            </>
          )}

          <div className="flex items-center justify-between mb-2.5 px-0.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: 'rgb(var(--c-primary))' }}>
              Ma production ({produits.length})
            </p>
            <button onClick={onOpenOffers} className="text-[10.5px] font-bold" style={{ color: 'rgb(var(--c-primary))' }}>
              ↺ Gérer mes offres ›
            </button>
          </div>
          {produits.length > 0 ? (
            <div className="grid grid-cols-2 gap-2.5">
              {produits.map((o) => (
                <div key={o.id} className="glass-surface rounded-[18px] p-3 flex items-center gap-2.5">
                  <span className="w-10 h-10 rounded-lg bg-ink/[0.06] flex items-center justify-center flex-shrink-0 overflow-hidden text-[22px]">
                    {o.product?.photos?.length > 0
                      ? <img src={fixImageUrl(o.product.photos[0])} alt="" className="w-full h-full object-cover rounded-lg" />
                      : (o.product?.category?.emoji || '🥭')}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-ink truncate">{o.product?.nom}</p>
                    <p className="text-[11px] font-bold" style={{ color: 'rgb(var(--c-primary))' }}>
                      {o.prix != null ? `${Number(o.prix).toFixed(2).replace('.', ',')}€/${o.prixUnit || 'kg'}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass-surface rounded-[18px] p-4 text-center">
              <p className="text-[12.5px] text-ink/60 leading-relaxed">
                Aucun produit au canal Évènements pour l'instant. Activez le canal <strong>Évènements</strong> sur
                vos offres pour qu'ils apparaissent ici et côté client.
              </p>
              <button onClick={onOpenOffers}
                className="mt-3 px-4 py-2.5 rounded-full text-[13px] font-extrabold text-ink"
                style={{ background: 'rgb(var(--c-primary))' }}>
                Gérer mes offres ›
              </button>
            </div>
          )}

          {/* ===== MES CERTIFICATIONS : preuve → vérif → badge ===== */}
          <CertifsSection certifs={certifs} onRefresh={load} />

          {/* ===== COORDONNEES & LEGAL (S61) — descendues de l'ancienne page
              « Mon organisation », supprimee du menu : SIRET, telephone,
              adresse et site web n'avaient qu'ELLE comme chemin d'edition. ===== */}
          <CoordonneesSection org={org} onRefresh={load} />
        </>
      )}

      {edit && (
        <div className="flex flex-col gap-3.5">
          {/* Photo de l'exploitation */}
          <div className="glass-surface rounded-[20px] p-4 flex items-center gap-3.5">
            <AvatarExploit photo={org?.logo} size={58} />
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-bold text-ink">Photo de l'exploitation</p>
              <p className="text-[11.5px] text-ink/55 mt-0.5">Visible par les clients sur votre fiche.</p>
            </div>
            {org?.logo ? (
              <button onClick={removePhoto} disabled={uploading}
                className="glass-surface rounded-full px-3 py-2 text-[12px] font-bold text-[#d9534f]">
                {uploading ? '...' : 'Retirer'}
              </button>
            ) : (
              <label className="rounded-full px-3.5 py-2 text-[12px] font-extrabold text-ink cursor-pointer"
                style={{ background: 'rgb(var(--c-primary))' }}>
                {uploading ? '...' : '+ Ajouter'}
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])} disabled={uploading} />
              </label>
            )}
          </div>

          <div className="glass-surface rounded-[20px] p-4">
            <p className="text-[11px] font-bold text-ink/50 mb-1.5">Nom de l'exploitation</p>
            <input value={form.nomCommercial}
              onChange={(e) => setForm((f) => ({ ...f, nomCommercial: e.target.value }))}
              className="w-full text-[16px] px-3.5 py-3 rounded-[13px] text-ink"
              style={{ border: '1px solid rgb(var(--c-green) / 0.15)', background: 'rgba(255,255,255,0.75)' }} />
            <p className="text-[11px] font-bold text-ink/50 mt-3.5 mb-1.5">Commune</p>
            <input value={form.ville}
              onChange={(e) => setForm((f) => ({ ...f, ville: e.target.value }))}
              className="w-full text-[16px] px-3.5 py-3 rounded-[13px] text-ink"
              style={{ border: '1px solid rgb(var(--c-green) / 0.15)', background: 'rgba(255,255,255,0.75)' }} />
          </div>

          {/* Presentation : 3 questions guidees quand elle est vide (S62) */}
          <PresentationGuidee value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} />

          <div className="glass-surface rounded-2xl px-4 py-3 flex gap-2.5 items-start"
            style={{ background: 'rgb(var(--c-teal) / 0.08)' }}>
            <span className="text-[15px]">↺</span>
            <p className="text-[12px] text-ink/65 leading-relaxed">
              La <strong>production</strong> et les <strong>certifications</strong> ne se modifient pas ici :
              elles viennent de vos offres et de votre dossier exploitation.
            </p>
          </div>

          {error && <p className="text-[12px] text-[#d9534f] text-center">{error}</p>}

          <button onClick={save} disabled={saving}
            className="w-full py-4 rounded-2xl text-[15px] font-bold text-creme disabled:opacity-60"
            style={{ background: 'rgb(var(--c-green))' }}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      )}
    </div>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageProfilProducteur() {
  // S62 : onglet de la barre (pas de retour) — ou sous-page du Compte si la
  // barre garde le Compte (MVP.COMPTE_DANS_BARRE_PRODUCTEUR).
  return <PageHub retour={MVP.COMPTE_DANS_BARRE_PRODUCTEUR ? 'account' : null}><ContenuProfilProducteur /></PageHub>
}

